/**
 * DemographicHeatmap — shell port of SSP's `DemographicHeatmap.tsx`.
 * Real demographic data sourcing is a future epic; this component
 * exists so the toggle, layer-id contract, and empty-state UX are in
 * place at parity with SSP.
 *
 * Behaviour:
 *   - When `visible={false}`: no map layer; component renders nothing.
 *   - When `visible={true}` : adds a low-opacity solid fill polygon
 *     covering the visible map bounds (the "fake gradient overlay"),
 *     and renders an HTML empty-state card overlay informing the user
 *     that demographic data integration is coming.
 *
 * The layer covers the current map bounds at mount/visibility-change
 * (no live recompute on pan/zoom — this is a shell, not a real
 * heatmap).
 *
 * Mount this inside the `MapContainer`'s children alongside other
 * map-aware components. The empty-state card uses absolute positioning
 * relative to the map container; pass `className` to override the
 * default centred placement.
 *
 * Linear: MYD-225 (I4).
 */

import { useEffect, useRef } from 'react'
import type maplibregl from 'maplibre-gl'
import type { Feature, FeatureCollection, Polygon } from 'geojson'
import { Info } from 'lucide-react'
import { cn } from 'lib/utils'

const SOURCE_ID = 'demographic-heatmap-source'
const LAYER_ID_FILL = 'demographic-heatmap-fill-layer'

const DEFAULT_FILL = '#f97316' // orange — placeholder demographic tint
const DEFAULT_OPACITY = 0.18

export interface DemographicHeatmapProps {
	map: maplibregl.Map | null
	isMapLoaded: boolean
	visible: boolean
	fillColor?: string
	opacity?: number
	emptyStateMessage?: string
	className?: string
}

export const DEMOGRAPHIC_EMPTY_STATE_MESSAGE =
	'Demographic data integration coming in a future release.'

function buildBoundsPolygon(map: maplibregl.Map): Feature<Polygon> | null {
	try {
		const bounds = map.getBounds()
		if (!bounds) return null
		const sw = bounds.getSouthWest()
		const ne = bounds.getNorthEast()
		// Counter-clockwise ring (GeoJSON polygon convention)
		const ring: [number, number][] = [
			[sw.lng, sw.lat],
			[ne.lng, sw.lat],
			[ne.lng, ne.lat],
			[sw.lng, ne.lat],
			[sw.lng, sw.lat],
		]
		return {
			type: 'Feature',
			properties: {},
			geometry: { type: 'Polygon', coordinates: [ring] },
		}
	} catch {
		return null
	}
}

export function DemographicHeatmap({
	map,
	isMapLoaded,
	visible,
	fillColor = DEFAULT_FILL,
	opacity = DEFAULT_OPACITY,
	emptyStateMessage = DEMOGRAPHIC_EMPTY_STATE_MESSAGE,
	className,
}: DemographicHeatmapProps) {
	const initialized = useRef(false)

	useEffect(() => {
		if (!map || !isMapLoaded) return

		const features: Feature<Polygon>[] = []
		if (visible) {
			const poly = buildBoundsPolygon(map)
			if (poly) features.push(poly)
		}
		const collection: FeatureCollection<Polygon> = {
			type: 'FeatureCollection',
			features,
		}
		const existingSource = map.getSource(SOURCE_ID) as
			| maplibregl.GeoJSONSource
			| undefined

		if (!initialized.current || !existingSource) {
			if (map.getLayer(LAYER_ID_FILL)) map.removeLayer(LAYER_ID_FILL)
			if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)
			map.addSource(SOURCE_ID, { type: 'geojson', data: collection })
			map.addLayer({
				id: LAYER_ID_FILL,
				type: 'fill',
				source: SOURCE_ID,
				paint: {
					'fill-color': fillColor,
					'fill-opacity': opacity,
				},
			})
			initialized.current = true
		} else {
			existingSource.setData(collection)
		}
	}, [map, isMapLoaded, visible, fillColor, opacity])

	// Cleanup on unmount.
	useEffect(() => {
		return () => {
			if (!map) return
			try {
				if (map.getLayer(LAYER_ID_FILL)) map.removeLayer(LAYER_ID_FILL)
				if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID)
				initialized.current = false
			} catch {
				// map may already be torn down
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	if (!visible) return null

	return (
		<div
			className={cn(
				'pointer-events-none absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10',
				className,
			)}
		>
			<div className="pointer-events-auto inline-flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/95 backdrop-blur shadow-sm px-3 py-2 max-w-xs text-xs text-amber-800">
				<Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
				<span>{emptyStateMessage}</span>
			</div>
		</div>
	)
}

export default DemographicHeatmap
